declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
  }
}

const GTM_ID = (import.meta.env.VITE_GTM_ID || '').trim()

let gtmLoaded = false
let analyticsEnabled = false

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function isConfigured(): boolean {
  return GTM_ID.length > 0
}

function ensureDataLayer(): void {
  if (!isBrowser()) return
  window.dataLayer = window.dataLayer || []
}

/**
 * gtag shim — required for GTM Consent Mode v2 and Google's tag detection.
 * Must push the native `arguments` object (not a rest-parameter Array).
 * GTM ignores Array-shaped consent commands, so analytics_storage never updates.
 */
function ensureGtag(): (...args: unknown[]) => void {
  ensureDataLayer()
  const w = window as Window & { gtag?: (...args: unknown[]) => void }
  if (!w.gtag) {
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments as unknown as Record<string, unknown>)
    }
  }
  return w.gtag
}

function pushConsentDefault(): void {
  ensureGtag()('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
}

function pushConsentUpdate(granted: boolean): void {
  const state = granted ? 'granted' : 'denied'
  ensureGtag()('consent', 'update', {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  })
}

/** Whether optional analytics cookies were accepted and GTM may fire events. */
export function isAnalyticsEnabled(): boolean {
  return analyticsEnabled
}

export function updateConsent(granted: boolean): void {
  if (!isConfigured() || !isBrowser()) return

  analyticsEnabled = granted
  pushConsentUpdate(granted)

  if (granted && !gtmLoaded) {
    loadGtm()
  }
}

export function loadGtm(): void {
  if (!isConfigured() || !isBrowser() || gtmLoaded) return

  gtmLoaded = true
  ensureDataLayer()
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`
  document.head.appendChild(script)
}

/**
 * Initialise analytics. GTM loads on page init with consent denied (Consent Mode v2)
 * so Google Tag Assistant can detect the container. Analytics events fire only after accept.
 *
 * Pass stored consent: `true` accepted, `false` rejected, `null` not yet chosen.
 */
export function initAnalytics(consentGranted: boolean | null): void {
  if (!isConfigured() || !isBrowser()) return

  pushConsentDefault()

  // Load GTM unless user previously rejected — keeps tag detectable for Google tests.
  if (consentGranted !== false) {
    loadGtm()
  }

  if (consentGranted === true) {
    analyticsEnabled = true
    pushConsentUpdate(true)
  } else if (consentGranted === false) {
    analyticsEnabled = false
    pushConsentUpdate(false)
  } else {
    analyticsEnabled = false
  }
}

export function pushEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!isConfigured() || !analyticsEnabled || !isBrowser()) return

  ensureDataLayer()
  window.dataLayer.push({
    event: name,
    page_path: window.location.pathname,
    ...params,
  })
}

/** SPA virtual page view — call from router afterEach. */
export function trackPageView(path: string, title?: string): void {
  if (!isConfigured() || !analyticsEnabled || !isBrowser()) return

  ensureDataLayer()
  window.dataLayer.push({
    event: 'page_view',
    page_path: path,
    page_title: title || document.title,
    page_location: `${window.location.origin}${path}`,
  })
}

/** Bridge selected backend tracking events to GA4 via GTM. */
export function bridgeToAnalytics(event: string, params: Record<string, unknown> = {}): void {
  pushEvent(event, params)
}
